import React, { useRef, useState } from "react";
import { EventCard } from "@/components/rider/EventCard";
import { Button } from "@/components/ui/button";
import { CommunityEvent } from "@/types/rider";
import { ChevronLeft, ChevronRight, Calendar, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

interface AddOn {
  id: string;
  description: string;
  price: number;
  enabled: boolean;
}

interface DesktopEventSelectorProps {
  events: CommunityEvent[];
  activeIndex: number;
  selectedEventId: string | null;
  isImmersive: boolean;
  setIsImmersive: (value: boolean) => void;
  handleEventSelection: (eventId: string, index: number) => void;
  handlePrevCard: () => void;
  handleNextCard: () => void;
  handleCardTap: (eventId: string, index: number) => void;
  handleContinue: (selectedAddOns: AddOn[], totalPrice: number) => void;
  handleManualEntry: () => void;
  handleResetSelection: () => void;
}

export const DesktopEventSelector = ({
  events,
  activeIndex,
  selectedEventId,
  isImmersive,
  setIsImmersive,
  handleEventSelection,
  handlePrevCard,
  handleNextCard,
  handleCardTap,
  handleContinue,
  handleManualEntry,
  handleResetSelection
}: DesktopEventSelectorProps) => {
  const selectedEvent = events.find(event => event.id === selectedEventId);
  const cardsContainerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  return (
    <div className="hidden md:block container mx-auto px-4 py-6 max-w-7xl">
      <div className="flex justify-between items-center mb-8">
        <button 
          onClick={handleResetSelection}
          className="flex items-center text-black hover:text-gray-700 transition-colors"
        >
          <ArrowLeft className="mr-1 h-5 w-5" />
          <span>View All Options</span>
        </button>
        <h1 className="text-3xl font-bold text-center text-black">Book A Party Bus To</h1>
        <div></div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 mt-16">
        <div className="relative h-[500px] flex items-center justify-center">
          <div className={`relative w-[350px] h-[450px] transition-all duration-300 ${isImmersive ? 'scale-110' : ''}`}>
            <AnimatePresence>
              {events.map((event, index) => {
                const isActive = index === activeIndex;
                const distance = Math.abs(index - activeIndex);
                const zIndex = events.length - distance;
                
                if (distance > 2) return null;

                const shadowColors = {
                  "Coachella": "rgba(255, 122, 89, 0.15)",
                  "EDC": "rgba(128, 0, 255, 0.15)",
                  "Beyond Wonderland": "rgba(0, 183, 255, 0.15)",
                  "Los Angeles Lakers": "rgba(85, 37, 130, 0.15)",
                  "Rufus Du Sol": "rgba(255, 198, 41, 0.15)"
                };
                
                const defaultShadow = "rgba(0, 0, 0, 0.1)";
                const shadowColor = shadowColors[event.name as keyof typeof shadowColors] || defaultShadow;
                
                return (
                  <motion.div
                    key={event.id}
                    className="absolute top-0 left-0 w-full h-full cursor-pointer"
                    style={{ 
                      zIndex,
                      boxShadow: `0 10px 20px ${shadowColor}`,
                      borderRadius: '24px',
                    }}
                    initial={{ 
                      x: distance * 80,
                      rotateZ: index > activeIndex ? 5 : (index < activeIndex ? -5 : 0),
                      y: distance * 25,
                      scale: 1 - (distance * 0.1),
                      opacity: 1 - (distance * 0.3)
                    }}
                    animate={{ 
                      x: distance * 80,
                      rotateZ: index > activeIndex ? 5 : (index < activeIndex ? -5 : 0),
                      y: distance * 25,
                      scale: isActive ? (isImmersive ? 1.1 : 1) : 1 - (distance * 0.1),
                      opacity: 1 - (distance * 0.3)
                    }}
                    exit={{ 
                      opacity: 0,
                      scale: 0.8,
                      transition: { duration: 0.2 }
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 300,
                      damping: 30
                    }}
                    onClick={() => handleCardTap(event.id, index)}
                    whileHover={{ scale: isActive ? 1.05 : 1 }}
                  >
                    <EventCard 
                      event={event} 
                      isSelected={event.id === selectedEventId} 
                      onClick={() => {}}
                    />
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
          
          <div className={`absolute left-1/2 -translate-x-1/2 flex space-x-4 mt-6 transition-opacity duration-300 ${isImmersive ? 'opacity-0' : 'opacity-100'}`} style={{ bottom: '-30px' }}>
            <button 
              className="bg-black text-white p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handlePrevCard}
              disabled={activeIndex === 0}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button 
              className="bg-black text-white p-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleNextCard}
              disabled={activeIndex === events.length - 1}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          
          <div className={`text-center mt-4 transition-opacity duration-300 ${isImmersive ? 'opacity-0' : 'opacity-100'}`}>
            <p className="text-sm text-gray-500">Tap To See Details</p>
          </div>
        </div>
        
        <EventDetailsPanel 
          selectedEvent={selectedEvent}
          handleContinue={handleContinue}
        />
      </div>
      
      <div className="flex justify-center pb-8">
        <button 
          onClick={handleManualEntry}
          className="text-black underline hover:text-neon-green transition-colors text-lg"
        >
          Enter Trip Request Manually
        </button>
      </div>
    </div>
  );
};

interface EventDetailsPanelProps {
  selectedEvent: CommunityEvent | undefined;
  handleContinue: (selectedAddOns: AddOn[], totalPrice: number) => void;
}

const EventDetailsPanel = ({ selectedEvent, handleContinue }: EventDetailsPanelProps) => {
  const [addOns, setAddOns] = useState<AddOn[]>([
    {
      id: '1',
      description: 'Ship Wrek @ The Grand After Party',
      price: 0,
      enabled: false
    },
    {
      id: '2', 
      description: 'DJ Diesel @ Big Night Live After Party',
      price: 0,
      enabled: false
    },
    {
      id: '3',
      description: 'Professional Photography Service',
      price: 0,
      enabled: false
    }
  ]);

  const basePrice = selectedEvent?.price || 1200;
  const totalPrice = basePrice + addOns.filter(addon => addon.enabled && addon.price > 0).reduce((sum, addon) => sum + addon.price, 0);

  const toggleAddOn = (id: string) => {
    setAddOns(prev => prev.map(addon => 
      addon.id === id ? { ...addon, enabled: !addon.enabled } : addon
    ));
  };

  if (!selectedEvent) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-xl text-gray-500">Select an event to see details</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-16">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="bg-white rounded-xl p-6 shadow-lg"
      >
        <h2 className="text-3xl font-bold text-black mb-2">{selectedEvent.name}</h2>
        <div className="flex items-center mb-4 text-gray-600">
          <Calendar className="h-5 w-5 mr-2" />
          <span>{selectedEvent.date?.toUpperCase().replace('/', '/').replace(' ', ', ')}-2025</span>
        </div>
        
        <div className="border-t border-gray-200 my-4 pt-4">
          <p className="text-gray-700 text-lg mb-4">{selectedEvent.startTime?.replace(' | ', ' - ').replace('Capacity', 'Up To').replace('Reservation', '').trim()}</p>
          
          <div className="flex justify-between items-center mb-6 border border-gray-200 rounded-lg p-4">
            <div>
              <p className="text-2xl font-bold text-black">${totalPrice}</p>
              <p className="text-gray-500">Price</p>
            </div>
            <div className="text-right">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-6 h-6 text-black" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                  </svg>
                ))}
              </div>
              <p className="text-gray-500">5 Star Rating</p>
            </div>
          </div>

          {/* Add-ons Section - Only show for Round Trip events */}
          {selectedEvent.startTime?.includes('Round Trip') && (
            <div className="mb-6 space-y-3">
              {selectedEvent.date?.includes('Friday') ? (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-black font-medium">Take Me To Ship Wrek @ The Grand After Party</p>
                    <p className="text-gray-600 text-sm">Free - $0</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={addOns[0]?.enabled || false}
                      onChange={() => toggleAddOn('1')}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              ) : selectedEvent.date?.includes('Saturday') ? (
                <div className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                  <div className="flex-1">
                    <p className="text-black font-medium">Take Me To DJ Diesel @ Big Night Live After Party</p>
                    <p className="text-gray-600 text-sm">Free - $0</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={addOns[1]?.enabled || false}
                      onChange={() => toggleAddOn('2')}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                </div>
              ) : null}
            </div>
          )}
          
          <div className="flex justify-center">
            <Button 
              className="bg-white text-black border border-black hover:bg-black hover:text-white rounded-full py-6 px-12 text-lg 
                 group relative overflow-hidden"
              onClick={() => handleContinue(addOns.filter(addon => addon.enabled), totalPrice)}
            >
              Book Today <span className="ml-2">🚌</span>
            </Button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
